import {FormEvent, PropsWithChildren, useContext, useEffect, useRef, useState} from 'react';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {css} from '@emotion/css';
import {isEqual} from 'lodash-es';
import {AxiosError} from 'axios';
import {colors, sizes} from 'patient-ping-remedy/packages/theme';
import {ToastType} from 'patient-ping-remedy/packages/toast';
import Button, {StyleType} from 'patient-ping-remedy/packages/button';
import SlideInPane from 'patient-ping-remedy/packages/slide-in-pane';
import Checkbox from 'patient-ping-remedy/packages/checkbox';
import Modal from 'patient-ping-remedy/packages/modal';
import Input from 'patient-ping-remedy/packages/input';
import Badge from 'patient-ping-remedy/packages/badge';
import {useAlertStore} from '@/store/alert_store';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import {FilterRequest, FilterResponse, FilterView} from '@/api/dto/filters';
import {FilterArgs} from '@common/FilterComponent';
import {createFormFieldConfig, useForm} from '@/hooks/useForm';
import {GENERICERROR} from '@manual_encounters/constants/BannerMessages';
import Helpers from '@/helpers/helpers';
import Validate from '@/helpers/validate';
import {
  FooterButtonWrapper,
  HeaderButtonWrapper,
  SelectedTypography,
  slideInPaneWithoutOverlay
} from '@common/FilterDrawer.styles';
import ConfirmModal from '@common/ConfirmModal';
import {log} from '@/helpers/logger';
import {MixpanelEventType, MixpanelFilterEvent} from "@/api/dto/mixpanel";
import {useMixpanel} from "@/hooks/useMixpanel";
import {useRosterStore} from "@/store/roster_store";

type Props = PropsWithChildren & {
  isOpen: boolean;
  close(): void;
  id: string;
  filterArgs: FilterArgs;
  filtersApplied: number;
  savedFilter?: FilterResponse;
  savableFilters?: boolean;
  pinnedFilters?: boolean;
  pinnedCount?: number;
  pinnedLabel?: string;
  maxPinned?: number;
  initialFilters?: Record<string, unknown>;
  onSave?(filter: FilterResponse): void;
  onReset?(filters: object): void;
}

export default function FilterDrawer({
  isOpen,
  close,
  id,
  filterArgs,
  filtersApplied,
  savedFilter,
  savableFilters,
  pinnedFilters,
  pinnedCount,
  pinnedLabel = 'Pin view',
  maxPinned = 6,
  initialFilters: prePopulatedFilters,
  onSave,
  onReset,
  children,
}: Props) {
  const { carecoApi } = useContext(CarecoApiContext);
  const { addAlert } = useAlertStore();
  const queryClient = useQueryClient();

  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [createFilterModalOpen, setCreateFilterModalOpen] = useState(false);
  const [initialFilters, setInitialFilters] = useState<object | null>(null);
  const [initialSavedFilters, setInitialSavedFilters] = useState<Record<string, unknown> | null>(null);
  const prevSavedFilterIdRef = useRef<number | undefined>(savedFilter?.id);
  const currentRoster = useRosterStore((state) => state.currentRoster);

  const {mixpanelEventProperties, getCurrentGroupDetails} = useMixpanel();

  const { mutateAsync: saveFilter, isPending } = useMutation({
    mutationFn: async (filter: FilterRequest) => {
      if (savedFilter) {
        const response = await carecoApi?.updateFilter(savedFilter.id, filter);
        postMixpanelEvent(response, MixpanelEventType.BIH_SAVED_FILTER_EDITED);
        return response;
      } else {
        const response = await carecoApi?.createFilter(filter);
        postMixpanelEvent(response, MixpanelEventType.BIH_SAVED_FILTER_CREATED);
        return response;
      }
    },
    onSuccess(filter) {
      addAlert({
        type: ToastType.SUCCESS,
        content: `${filter?.name} saved successfully.`,
      });
      void queryClient.invalidateQueries({ queryKey: ['filters', id] });
      setCreateFilterModalOpen(false);
      close();
    },
    onError(error: AxiosError) {
      Helpers.alertOnError(`Failed to save view. ${GENERICERROR} ${Helpers.traceId(error)}`, error);
    },
  });

  const expandFilterKeys = (filters: Record<string, unknown>): string[] => {
    return Object.entries(filters).flatMap(([key, value]) => {
      if (Array.isArray(value)) {
        return value.length > 0 ? [key] : [];
      }
      if (value && typeof value === 'object') {
        // Expand date range objects (e.g. dueDate -> startDate, endDate)
        return Object.entries(value)
          .filter(([_, v]) => !!v)
          .map(([subKey]) => subKey);
      }
      return value ? [key] : [];
    });
  };

  const postMixpanelEvent = (response: FilterResponse | undefined, eventName: MixpanelEventType) => {
    if (!response) return;

    const currentGroupDetails = currentRoster ? getCurrentGroupDetails(currentRoster) : null;

    const filterFieldsUsed = expandFilterKeys(filterArgs.currentFilters as Record<string, unknown>);
    const { fieldsAdded, fieldsRemoved } = getFieldsChanged();

    const event: MixpanelFilterEvent = {
      eventName: eventName,
      filterId: response?.id,
      filterName: response?.name,
      filterOwnerId: response?.ownerId,
      filterCriteriaCount: filtersApplied,
      ...mixpanelEventProperties!,
      ...currentGroupDetails,
      filterFieldsUsed: filterFieldsUsed,
      filterView: response.view,
      fieldsAdded: fieldsAdded,
      fieldsRemoved: fieldsRemoved
    };

    carecoApi?.postMixpanelEvent(event);
  };

  const getFieldsChanged = () => {
    const initial = (initialSavedFilters ?? {}) as Record<string, unknown>;
    const current = filterArgs.currentFilters as Record<string, unknown>;

    const initialKeys = expandFilterKeys(initial);
    const currentKeys = expandFilterKeys(current);

    const fieldsAdded = currentKeys.filter(k => !initialKeys.includes(k));
    const fieldsRemoved = initialKeys.filter(k => !currentKeys.includes(k));

    // Include pre-populated filter fields that still have values
    if (!savedFilter && prePopulatedFilters) {
      const prePopulatedKeys = expandFilterKeys(prePopulatedFilters);
      for (const key of prePopulatedKeys) {
        if (!fieldsAdded.includes(key)) {
          const value = current[key];
          if (Array.isArray(value) ? value.length > 0 : !!value) {
            fieldsAdded.push(key);
          }
        }
      }
    }

    return {
      fieldsAdded,
      fieldsRemoved
    };
  };

  const { form, setInitialForm, isValid, isFormChanged, handleChange, handleSubmit } = useForm(async () => {
    try {
      const filter = await saveFilter({
        view: id as FilterView,
        name: form.name.value,
        data: JSON.stringify(filterArgs.currentFilters),
        isDefault: form.is_default.value,
        isPinned: form.is_pinned.value,
      });
      if (filter) {
        onSave?.(filter);
      }

      if (filter?.isPinned && !savedFilter?.isPinned) {
        postMixpanelEvent(filter, MixpanelEventType.BIH_SAVED_FILTER_PINNED);
      }

    } catch (error) {
      log('error', error);
    }
  });

  useEffect(() => {
    if (isOpen) {
      setInitialForm({
        ...createFormFieldConfig(
          'name',
          savedFilter?.name ?? '',
          { valid: true, message: '' },
          [Validate.required],
          true,
        ),
        ...createFormFieldConfig(
          'is_default',
          savedFilter?.isDefault ?? false,
          { valid: true, message: '' },
          [],
          false,
        ),
        ...createFormFieldConfig(
          'is_pinned',
          savedFilter?.isPinned ?? false,
          { valid: true, message: '' },
          [],
          false,
        ),
      });
    }
  }, [isOpen]);

  useEffect(() => {
    setInitialFilters(null);
    const parsedFilterData = savedFilter ? Helpers.safeJsonParse(savedFilter.data, {}) as Record<string, unknown> : null;
    setInitialSavedFilters(parsedFilterData);

    if (savedFilter && savedFilter.id !== prevSavedFilterIdRef.current) {
      const currentGroupDetails = currentRoster ? getCurrentGroupDetails(currentRoster) : null;
      const filterFieldsUsed = expandFilterKeys(parsedFilterData || {});

      const event: MixpanelFilterEvent = {
        eventName: MixpanelEventType.BIH_SAVED_FILTER_USED,
        filterId: savedFilter.id,
        filterName: savedFilter.name,
        filterOwnerId: savedFilter.ownerId,
        filterCriteriaCount: filterFieldsUsed.length,
        filterFieldsUsed,
        filterView: savedFilter.view,
        fieldsAdded: [],
        fieldsRemoved: [],
        ...mixpanelEventProperties!,
        ...currentGroupDetails,
      };

      carecoApi?.postMixpanelEvent(event);
    }

    prevSavedFilterIdRef.current = savedFilter?.id;
  }, [savedFilter]);

  useEffect(() => {
    if (isOpen && !initialFilters) {
      setInitialFilters(filterArgs.currentFilters);
    }
  }, [isOpen, initialFilters]);

  const handleDrawerClose = () => {
    if (isOpen && savedFilter && !isPending && (isFormChanged() || isFiltersChanged)) {
      setConfirmModalOpen(true);
    } else {
      close();
    }
  };

  const handleDrawerSubmit = (e: FormEvent) => {
    if (savedFilter) {
      handleSubmit(e);
    } else {
      setCreateFilterModalOpen(true);
    }
  };

  const isFiltersChanged = !isEqual(initialFilters, filterArgs.currentFilters);
  const modalDisabled = isPending || !isValid() || !isFormChanged();
  const drawerDisabled = isPending || (savedFilter
    ? !isValid() || (!isFormChanged() && !isFiltersChanged)
    : !isFiltersChanged);

  const nameInput = (
    <Input
      labelText={savedFilter ? 'Saved view name' : 'View name'}
      value={form.name?.value ?? ''}
      error={!form.name?.valid.valid}
      errorMessage={form.name?.valid.message}
      onChange={(e) => handleChange(e, 'name', e.target.value)}
    />
  );

  return (
    <>
      <SlideInPane
        isOpen={isOpen}
        onClose={handleDrawerClose}
        headerText={savedFilter ? `Edit: ${savedFilter.name}` : 'Filters'}
        headerChildren={savableFilters && (
          <HeaderButtonWrapper>
            <Button disabled={drawerDisabled} onClick={handleDrawerSubmit}>
              Save
            </Button>
          </HeaderButtonWrapper>
        )}
        buttons={[
          <FooterButtonWrapper key="footer">
            <SelectedTypography>
              Selected: <Badge data-testid="filter-count" backgroundColor={colors.blue4}>{filtersApplied}</Badge>
            </SelectedTypography>
            {onReset && (
              <Button styleType={StyleType.TERTIARY} onClick={() => onReset?.({})}>
                Clear all
              </Button>
            )}
          </FooterButtonWrapper>
        ]}
      >
        <style>
          {slideInPaneWithoutOverlay.styles}
        </style>
        {savedFilter && (
          <>
            {nameInput}
            <Checkbox
              label="Set as a default view"
              value={form.is_default?.value}
              className={css({ marginBottom: sizes.medium })}
              onChange={(value, e) => handleChange(e, 'is_default', value)}
            />
            {pinnedFilters && (
              <Checkbox
                label={pinnedLabel}
                value={form.is_pinned?.value}
                disabled={!form.is_pinned?.value
                  && (pinnedCount ?? 0) - (savedFilter?.isPinned ? 1 : 0) >= maxPinned}
                className={css({ marginBottom: sizes.medium })}
                onChange={(value, e) => handleChange(e, 'is_pinned', value)}
              />
            )}
          </>
        )}
        {children}
      </SlideInPane>
      <ConfirmModal
        show={confirmModalOpen}
        showFunction={setConfirmModalOpen}
        leaveFunction={() => {
          close();
          onReset?.(initialFilters ?? {});
        }}
      />
      <Modal
        isOpen={createFilterModalOpen}
        headerText="Save view"
        buttons={[
          <Button key="cancel" onClick={() => setCreateFilterModalOpen(false)} styleType={StyleType.TERTIARY}>Cancel</Button>,
          <Button key="save" disabled={modalDisabled} onClick={handleSubmit}>Save</Button>
        ]}
        className={css({ width: '50rem' })}
      >
        {nameInput}
        {pinnedFilters && (
          <Checkbox
            label={pinnedLabel}
            value={form.is_pinned?.value}
            disabled={!form.is_pinned?.value
              && (pinnedCount ?? 0) - (savedFilter?.isPinned ? 1 : 0) >= maxPinned}
            className={css({ marginTop: sizes.small })}
            onChange={(value, e) => handleChange(e, 'is_pinned', value)}
          />
        )}
      </Modal>
    </>
  );
}
